import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  LinearProgress,
  Button,
} from '@mui/material';
import {
  People,
  AttachMoney,
  TrendingUp,
  Receipt,
} from '@mui/icons-material';
import api from '../../config/api';

interface DashboardStats {
  totalMembers: number;
  activeMembers: number;
  totalDuesCollected: number;
  pendingDues: number;
  monthlyExpenses: number;
  budgetUtilization: number;
}

export const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalMembers: 0,
    activeMembers: 0,
    totalDuesCollected: 0,
    pendingDues: 0,
    monthlyExpenses: 0,
    budgetUtilization: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // For now, we'll use placeholder data
      // In a real implementation, create a dashboard endpoint that aggregates this data
      setStats({
        totalMembers: 45,
        activeMembers: 42,
        totalDuesCollected: 125000,
        pendingDues: 15000,
        monthlyExpenses: 48000,
        budgetUtilization: 73,
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard: React.FC<{
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color: string;
  }> = ({ title, value, icon, color }) => (
    <Card elevation={2}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" component="h2">
              {value}
            </Typography>
          </Box>
          <Box sx={{ color: color }}>{icon}</Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {/* Statistics Cards */}
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' }, minWidth: { xs: '100%', sm: '250px' } }}>
          <StatCard
            title="Total Members"
            value={stats.totalMembers}
            icon={<People fontSize="large" />}
            color="#1976d2"
          />
        </Box>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' }, minWidth: { xs: '100%', sm: '250px' } }}>
          <StatCard
            title="Active Members"
            value={stats.activeMembers}
            icon={<People fontSize="large" />}
            color="#2e7d32"
          />
        </Box>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' }, minWidth: { xs: '100%', sm: '250px' } }}>
          <StatCard
            title="Dues Collected"
            value={`₺${stats.totalDuesCollected.toLocaleString()}`}
            icon={<AttachMoney fontSize="large" />}
            color="#0288d1"
          />
        </Box>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' }, minWidth: { xs: '100%', sm: '250px' } }}>
          <StatCard
            title="Pending Dues"
            value={`₺${stats.pendingDues.toLocaleString()}`}
            icon={<AttachMoney fontSize="large" />}
            color="#ed6c02"
          />
        </Box>

        {/* Budget Utilization */}
        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' }, minWidth: { xs: '100%', md: '300px' } }}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Budget Utilization
            </Typography>
            <Box display="flex" alignItems="center" mb={1}>
              <Box width="100%" mr={1}>
                <LinearProgress
                  variant="determinate"
                  value={stats.budgetUtilization}
                  sx={{ height: 10, borderRadius: 5 }}
                />
              </Box>
              <Box minWidth={35}>
                <Typography variant="body2" color="textSecondary">
                  {`${Math.round(stats.budgetUtilization)}%`}
                </Typography>
              </Box>
            </Box>
            <Typography variant="body2" color="textSecondary">
              {stats.budgetUtilization}% of annual budget utilized
            </Typography>
          </Paper>
        </Box>

        {/* Monthly Expenses */}
        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' }, minWidth: { xs: '100%', md: '300px' } }}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Monthly Expenses
            </Typography>
            <Box display="flex" alignItems="center">
              <Receipt fontSize="large" sx={{ color: '#d32f2f', mr: 2 }} />
              <Typography variant="h4">
                ₺{stats.monthlyExpenses.toLocaleString()}
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Current month expenses
            </Typography>
          </Paper>
        </Box>

        {/* Quick Actions */}
        <Box sx={{ flex: '1 1 100%' }}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<People />}
                href="/members/new"
              >
                Add Member
              </Button>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<Receipt />}
                href="/expenses/new"
              >
                Record Expense
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<AttachMoney />}
                href="/dues/payment"
              >
                Record Payment
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};
